import React, {useEffect, useMemo, useState} from "react";
import {Button, Card, Tag} from "antd";
import {CalendarOutlined, CrownOutlined} from "@ant-design/icons";
import {CelebrationDisplayItem, eventDataType} from "../type/celebrations.types";
import {formatDateToDayMonth, getImageUrl, getNameOnAvatar} from "../../../utility/utility";
import {WISHES} from "./celebration-message";
import WishEmailModal from "./wish-email-modal";
import dayjs from "dayjs";
import EmptyData from "../../common/empty-data/empty-data";
import {HeartPlus, Mail} from "lucide-react";
import {IWish} from "../../dashboard/celebrations/types/celebration-card.types";
import server from "../../../server";
import SendWishesModal from "../../dashboard/celebrations/utils/send-wishes-modal";
import {webSocketService} from "../../../services/WebSocketService";
import "../../../styles/component/celebrations/celebration.cards.scss";

interface CelebrationCardsProps {
    activeCategory: string;
    activeTimeFilter: string;
    birthdayData: eventDataType;
    anniversaryData: eventDataType;
}

const CelebrationCards = (props: CelebrationCardsProps) => {
    const [openModal, setModalOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [wishes, setWishes] = useState<IWish[]>([]);
    const [loadingWishes, setLoadingWishes] = useState(false);
    const dynamicWishText = props.activeTimeFilter === 'today' ? "Send Wish" : props.activeTimeFilter === "passed" ? "View Wishes" : "Send Wish";

    useEffect(() => {

        if (!open || !selectedEvent) {
            return;
        }

        // CREATE
        webSocketService.subscribeToTopic(
            "wishes",
            "/topic/wishes",
            (message) => {
                const wish = JSON.parse(message);

                if (wish.eventId !== selectedEvent.id) {
                    return;
                }

                setWishes(prev => {

                    const existingWish = prev.find(
                        item => item.id === wish.id);

                    // UPDATE existing wish
                    if (existingWish) {
                        return prev.map(item =>
                            item.id === wish.id
                                ? {
                                    ...item,
                                    ...wish
                                }
                                : item
                        );
                    }

                    // CREATE new wish
                    return [...prev, wish];
                });
            }
        );

        webSocketService.subscribeToTopic(
            "deleted-wishes",
            "/topic/wishes/deleted",
            (message) => {
                const deletedWish = JSON.parse(message);
                setWishes(wish =>
                    wish.filter(
                        wish => wish.id !== deletedWish.wishId
                    )
                );
            }
        );

        return () => {
            webSocketService.unsubscribeTopic("wishes");
            webSocketService.unsubscribeTopic("deleted-wishes");
        };
    }, [open, selectedEvent]);

    const getDaysDifference = (date: string) => {
        const celebrationDate = new Date(date);
        const today = new Date();

        celebrationDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return (celebrationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    };

    const shouldShowSendWishButton = (item: CelebrationDisplayItem) => {
        const diff = getDaysDifference(item.rawStartDate);

        switch (props.activeTimeFilter) {
            case "today":
                return true;

            case "upcoming":
                // Tomorrow (1) or Day after tomorrow (2)
                return diff === 1 || diff === 2;

            case "passed":
                // Yesterday (-1) or Day before yesterday (-2)
                return diff === -1 || diff === -2;

            default:
                return false;
        }
    };

    const getWishButtonText = (item: CelebrationDisplayItem) => {
        if (props.activeTimeFilter === "today") {
            return "Send Wish";
        }

        if (props.activeTimeFilter === "upcoming") {
            return "Send Wish";
        }

        if (props.activeTimeFilter === "passed") {
            return shouldShowSendWishButton(item)
                ? "Send Wish"
                : "View Wishes";
        }

        return "Send Wish";
    };

    const celebrations: CelebrationDisplayItem[] = useMemo(() => {

        const timeKey = props.activeTimeFilter as keyof eventDataType;

        const getRandomWish = (
            type: "BIRTHDAY" | "WORK_ANNIVERSARY",
            employeeId: string
        ) => {
            const wishes = WISHES[type];
            let hash = 0;
            for (let i = 0; i < employeeId.length; i++) {
                hash = (hash + employeeId.charCodeAt(i)) % wishes.length;
            }
            return wishes[hash];
        };

        const allItems: CelebrationDisplayItem[] = [
            ...(props.birthdayData?.[timeKey]?.map((item: any) => ({
                id: item.id,
                employeeId: item.employeeId,
                name: item.name,
                rawStartDate: item.startDate,
                startDate: formatDateToDayMonth(item.startDate),
                description: getRandomWish("BIRTHDAY", item.id),
                type: "BIRTHDAY" as const,
                designation: item.designation,
                profilePhoto: item.profilePhoto,
            })) || []),

            ...(props.anniversaryData?.[timeKey]?.map((item: any) => ({
                id: item.id,
                employeeId: item.employeeId,
                name: item.name,
                rawStartDate: item.startDate,
                startDate: formatDateToDayMonth(item.startDate),
                description: getRandomWish("WORK_ANNIVERSARY", item.id),
                type: "WORK_ANNIVERSARY" as const,
                yearsCompleted: item.yearsCompleted,
                designation: item.designation,
                profilePhoto: item.profilePhoto,
            })) || []),
        ];

        allItems.sort(
            (a, b) => dayjs(a.rawStartDate).valueOf() - dayjs(b.rawStartDate).valueOf()
        );

        if (props.activeCategory === "birthdays") {
            return allItems.filter(i => i.type === "BIRTHDAY");
        }
        if (props.activeCategory === "workAnniversaries") {
            return allItems.filter(i => i.type === "WORK_ANNIVERSARY");
        }
        return allItems;

    }, [props.activeCategory, props.activeTimeFilter, props.birthdayData, props.anniversaryData]);

    const handleWishModalOpen = async (employee: CelebrationDisplayItem) => {
        setLoadingWishes(true);
        try {
            setSelectedEvent(employee);
            setOpen(true);
            const data = await server.wish.listById(employee.id);
            setWishes(data);
        } catch (e) {
            console.log(e);
        } finally {
            setLoadingWishes(false);
        }
    };

    return (
        <>
            <div>
                {celebrations.length === 0 ? (
                    <EmptyData
                        icon={<span className={'celebration-empty-icon'}>🎉</span>}
                        title={"No celebrations!"}
                        subtitle={"Don't worry, there are exciting celebrations coming up soon"}/>
                ) : (
                    <div className="celebration-grid">
                        {celebrations.map((item) => (
                            <Card key={`${item.type}-${item.id}`} className="celebration-card">
                                <div className="card-header">
                                    <div className="avatar">
                                        {item.profilePhoto ? (
                                            <img
                                                src={getImageUrl(item.profilePhoto, 'small')}
                                                className={'profile-photo'}
                                                alt={item.name}
                                            />
                                        ) : (
                                            getNameOnAvatar(item.name)
                                        )}
                                    </div>

                                    <Tag
                                        className={`type-tag ${
                                            item.type === "BIRTHDAY"
                                                ? "birthday"
                                                : "anniversary"
                                        }`}
                                    >
                                        {item.type === "BIRTHDAY"
                                            ? "Birthday"
                                            : "Work Anniversary"}
                                    </Tag>
                                </div>

                                <h3 className="name">{item.name}</h3>
                                <p className="description">{item.designation}</p>

                                <div className="meta">
                                    <CalendarOutlined/>
                                    <span>{item.startDate}</span>

                                    {item.type === "WORK_ANNIVERSARY" && (
                                        <div className="completed-years">
                                            / <CrownOutlined/>
                                            <span>
                                                {item.yearsCompleted}{" "}
                                                {Number(item.yearsCompleted) > 1 ? "years" : "year"}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <p className="message">“{item.description}”</p>

                                <div className={'celebration-action-button'}>
                                    {(props.activeTimeFilter !== "upcoming" || shouldShowSendWishButton(item)) && (
                                        <Button
                                            className="send-wishes-button"
                                            onClick={() => handleWishModalOpen(item)}
                                        >
                                            <HeartPlus/> {getWishButtonText(item)}
                                        </Button>
                                    )}
                                    <Button
                                        className={'send-wishes-button'}
                                        onClick={() => {
                                            setSelectedEmployeeId(item.employeeId);
                                            setModalOpen(true);
                                        }}
                                    >
                                        <Mail/> Send Email
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            <WishEmailModal
                openModal={openModal}
                setModalOpen={setModalOpen}
                employeeId={selectedEmployeeId}
            />

            <SendWishesModal
                open={open}
                setOpen={setOpen}
                event={selectedEvent}
                wishes={wishes}
                loadingWishes={loadingWishes}
                isToday={dayjs(selectedEvent?.rawStartDate).isSame(dayjs(), "day")}
                activeTimeFilter={props.activeTimeFilter as "passed" | "today" | "upcoming"}
                canSendWish={
                    selectedEvent
                        ? shouldShowSendWishButton(selectedEvent)
                        : false
                }
            />
        </>
    );
};

export default CelebrationCards;
